/** Which of the two stored names is a name, and which is a handle.
 *
 *  `agents.full_name` is seeded from the email's local part whenever nobody
 *  supplied a real one — invite-agent, create-workspace, register-flow and
 *  super-admin-api all do `email.split("@")[0]`. After that the column cannot
 *  tell a handle from a name, and the person gets greeted as "cvasquez".
 *
 *  The signup metadata usually still holds the real name, so it is the better
 *  source exactly when the agents row is carrying the handle. */

function clean(value: string | null | undefined): string | null {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : null;
}

function handleOf(email: string | null | undefined): string | null {
  return clean(email)?.split("@")[0]?.toLowerCase() ?? null;
}

function isHandle(name: string | null, email: string | null | undefined): boolean {
  const handle = handleOf(email);
  return handle !== null && name !== null && name.toLowerCase() === handle;
}

export interface NameSources {
  agentFullName?: string | null;
  metadataFullName?: string | null;
  email?: string | null;
}

/** The person's real name, or null when every source is just their handle.
 *  Null is deliberate: a greeting reads better without a name than with a
 *  login. Callers that need to render an identity row can fall back on their
 *  own — see `displayNameOrHandle`. */
export function resolveDisplayName({ agentFullName, metadataFullName, email }: NameSources): string | null {
  for (const candidate of [clean(agentFullName), clean(metadataFullName)]) {
    if (candidate && !isHandle(candidate, email)) return candidate;
  }
  return null;
}

/** For rows that must show something: the real name, else the handle. */
export function displayNameOrHandle(sources: NameSources): string | null {
  return resolveDisplayName(sources) ?? clean(sources.agentFullName) ?? handleOf(sources.email);
}

/** What a greeting uses. Multi-word names are addressed by the first word. */
export function firstNameOf(sources: NameSources): string | null {
  return resolveDisplayName(sources)?.split(/\s+/)[0] ?? null;
}
